import React, { useEffect, useRef, useState } from 'react';

const MIN_GAP = 40;

// On desktop, pushes the bio down so its dotted divider lines up with the top of `alignTestId`
const useAlignedMargin = (alignTestId) => {
  const ref = useRef(null);
  const [margin, setMargin] = useState(MIN_GAP);
  useEffect(() => {
    const update = () => {
      const el = ref.current;
      const target = document.querySelector(`[data-testid="${alignTestId}"]`);
      if (!el || !target || window.innerWidth < 1024) {
        setMargin(MIN_GAP);
        return;
      }
      const current = parseFloat(getComputedStyle(el).marginTop) || 0;
      const naturalTop = el.getBoundingClientRect().top - current;
      const targetTop = target.getBoundingClientRect().top;
      setMargin(Math.max(MIN_GAP, Math.round(targetTop - naturalTop)));
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(document.body);
    window.addEventListener('resize', update);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', update);
    };
  }, [alignTestId]);
  return [ref, margin];
};

export const CoachBioCard = ({ alignTestId = 'daily-progress-card' }) => {
  const [ref, margin] = useAlignedMargin(alignTestId);
  return (
  <section ref={ref} style={{ marginTop: margin }} className="pt-6 md:pt-8 border-t-2 border-dashed border-[#E9C7A8]" data-testid="coach-bio-section">
    <p className="text-[10px] sm:text-[11px] uppercase tracking-[0.2em] text-[#B8871E] font-semibold mb-3">Meet Your Coach</p>
    <div className="bg-[#FBEDE3] border border-[#F0D3BF] rounded-xl md:rounded-2xl p-4 md:p-5" data-testid="coach-bio-card">
      <div className="w-full aspect-[4/3] rounded-xl overflow-hidden shadow-sm mb-4">
        <img
          src="https://customer-assets-eiarnc6j.emergentagent.net/job_39ce99f8-2df7-4a5a-9cad-1b3f94f84fe9/artifacts/ho59sw0f_image.png"
          alt="Yvette Styner, RHN"
          className="w-full h-full object-cover object-[50%_20%]"
          style={{ filter: 'saturate(0.85) brightness(1.05) contrast(0.95)' }}
        />
      </div>
      <h3 className="font-heading text-lg md:text-xl font-bold text-stone-900 mb-1">Hi ya! I&apos;m Yvette Styner, RHN</h3>
      <p className="text-stone-500 text-[11px] sm:text-xs mb-4 leading-relaxed">
        Registered Holistic Nutritionist&trade; · Menopause Metabolism Educator · Fellow Menopausal Woman
      </p>
      <p className="text-stone-700 text-xs sm:text-sm leading-relaxed mb-3">
        I created RxGRIT because knowing what to do isn&apos;t the same as doing it — and menopause makes that gap even wider. This is the daily support I wish every woman had beside her.
      </p>
      <p className="text-stone-700 text-xs sm:text-sm leading-relaxed mb-4">
        RxGRIT isn&apos;t a hands-off app. I&apos;m actively in here every day — answering questions, showing up in the community, and helping you stay accountable.
      </p>
      <p className="bg-white/70 text-stone-900 text-xs sm:text-sm font-medium leading-relaxed px-3 py-2 rounded-lg">
        <a href="mailto:yvette@buildnutrition.ca" className="underline hover:text-[#B8871E] transition-colors" data-testid="coach-bio-reach-out">Reach out</a> directly to me for help with anything, anytime. For real, I love hearing from you!
      </p>
    </div>
  </section>
  );
};
